import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import PrivacyChecks from "@/components/PrivacyChecks";
import { CountryFields, countrySet, countryValid } from "@/components/CountryPrompt";

export default function Onboarding() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [team, setTeam] = useState("");
  const [leader, setLeader] = useState("");
  const [name, setName] = useState(user?.name || "");
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [namesOk, setNamesOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checks, setChecks] = useState({ notice: false, pbo: false });
  const hasCountry = countrySet(user);
  const [country, setCountry] = useState({ country: "", other: "" });

  useEffect(() => {
    api.get("/reference").then((r) => setTeams(r.data.teams));
  }, []);

  useEffect(() => {
    if (user?.name && !name) setName(user.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.name]);

  useEffect(() => {
    if (!team) { setLeaders([]); return; }
    api.get("/leaders", { params: { team } }).then((r) => {
      // Exclude self from selectable list
      setLeaders((r.data || []).filter((l) => l.id !== user?.id));
    });
  }, [team, user?.id]);

  const isPendingSignup = user && !user.approved && user.role !== "admin";
  const NAME_OK = /^[\p{L}\p{M} &'’.,-]*$/u;
  const nameErr = (v, label) => (!v.trim() ? `${label} is required` : v.trim().length > 60 ? `${label} must be 60 characters or fewer`
    : (!NAME_OK.test(v) || /[.,'’&-]{3,}/.test(v) || !/\p{L}/u.test(v)) ? `${label} can only contain letters, spaces, &, apostrophes, hyphens, commas and full stops` : null);
  const namesInvalid = isPendingSignup && (!!nameErr(first, "Preferred first name") || !!nameErr(last, "Last name") || !namesOk);
  const hierarchyDone = user && (user.role === "admin" || user.role === "oversight" || (user.upline_team && user.nearest_upline_leader_id));
  if (user && user.onboarded && user.approved && hierarchyDone) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!team) { toast.error("Select your upline team"); return; }
    if (!leader) { toast.error("Select your nearest upline leader"); return; }
    if (isPendingSignup) {
      const err = nameErr(first, "Preferred first name") || nameErr(last, "Last name");
      if (err) { toast.error(err); return; }
      if (!namesOk) { toast.error("Please confirm your names are correct"); return; }
    } else if (!name.trim()) { toast.error("Please confirm your name"); return; }
    if (isPendingSignup && (!checks.notice || !checks.pbo)) { toast.error("Please tick both privacy statements"); return; }
    if (!hasCountry && !countryValid(country)) { toast.error("Please choose your country"); return; }
    setBusy(true);
    try {
      await api.post("/onboarding", {
        upline_team: team,
        nearest_upline_leader_id: leader || null,
        ...(isPendingSignup
          ? { first_name: first.trim(), last_name: last.trim(), names_confirmed: namesOk }
          : { name: name.trim() }),
        privacy_notice_ack: checks.notice,
        pbo_data_minimisation_ack: checks.pbo,
        ...(!hasCountry ? { country: country.country, country_other: country.country === "Other" ? country.other.trim() : null } : {}),
      });
      await refresh();
      if (isPendingSignup) {
        toast.success("Access request submitted");
        navigate("/pending-approval", { replace: true });
      } else {
        toast.success("You're all set");
        navigate("/", { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7F5EF] flex items-center justify-center p-6">
      <Card className="w-full max-w-lg p-6 sm:p-8" data-testid="onboarding-page">
        <div className="text-[11px] uppercase tracking-[0.24em] text-[#C9A961] font-semibold">
          {isPendingSignup ? "Request Access" : "Welcome"}
        </div>
        <h1 className="font-serif text-3xl text-[#0A1F44] mt-1">
          {isPendingSignup ? "Request Team Smith Hub access" : "Set up your Team Smith profile"}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Signed in as <span className="font-medium text-slate-800">{user?.email}</span>.
          {isPendingSignup
            ? " Confirm your details below — a Team Smith Admin will approve your access."
            : " Choose your team and the nearest recognised leader above you."}
        </p>

        {user?.picture && (
          <div className="mt-4 flex items-center gap-3">
            <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
            <div className="text-xs text-slate-500">Google profile photo</div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-5 mt-6">
          {isPendingSignup ? (
            <div className="space-y-3" data-testid="onboard-names">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="onboard-first">Preferred first name</Label>
                  <Input id="onboard-first" data-testid="onboard-first-name" value={first} maxLength={60} autoComplete="given-name"
                         onChange={(e) => { setFirst(e.target.value); setNamesOk(false); }} placeholder="e.g. John and Carol" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="onboard-last">Last name</Label>
                  <Input id="onboard-last" data-testid="onboard-last-name" value={last} maxLength={60} autoComplete="family-name"
                         onChange={(e) => { setLast(e.target.value); setNamesOk(false); }} placeholder="e.g. Stewart" />
                </div>
              </div>
              <div className="text-xs text-slate-500">Couples can enter both first names, e.g. “John and Carol”. This is how your name appears across the Hub.</div>
              {user?.name && user.name !== user.email && (
                <button type="button" data-testid="onboard-use-google-name" className="text-xs text-[#0A1F44] underline underline-offset-2"
                        onClick={() => { const p = user.name.trim().split(/\s+/); setFirst(p.length > 1 ? p.slice(0, -1).join(" ") : p[0]); setLast(p.length > 1 ? p[p.length - 1] : ""); setNamesOk(false); }}>
                  Fill in from my Google name ({user.name}) — you can edit it
                </button>
              )}
              <label className="flex items-start gap-2 text-sm text-slate-700 min-h-[44px]">
                <input type="checkbox" className="mt-1 w-4 h-4" data-testid="onboard-names-confirm" checked={namesOk}
                       disabled={!first.trim() || !last.trim()} onChange={(e) => setNamesOk(e.target.checked)} />
                <span>My preferred first name and last name above are correct{first.trim() && last.trim() ? <>: <b className="break-words">{`${first.trim()} ${last.trim()}`}</b></> : ""}</span>
              </label>
            </div>
          ) : (
          <div className="space-y-1.5">
            <Label>Your name</Label>
            <Input
              data-testid="onboard-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Alex & Jamie Nguyen"
            />
          </div>
          )}
          <div className="space-y-1.5">
            <Label>Upline team</Label>
            <Select value={team} onValueChange={(v) => { setTeam(v); setLeader(""); }}>
              <SelectTrigger data-testid="onboard-team"><SelectValue placeholder="Select team" /></SelectTrigger>
              <SelectContent>
                {teams.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Nearest upline leader</Label>
            <Select value={leader} onValueChange={setLeader} disabled={!team}>
              <SelectTrigger data-testid="onboard-leader">
                <SelectValue placeholder={team ? "Select the nearest recognised ATL leader above you" : "Choose team first"} />
              </SelectTrigger>
              <SelectContent>
                {leaders.length === 0 && <div className="p-2 text-xs text-slate-500">No leaders found for this team.</div>}
                {leaders.map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.name} <span className="text-slate-400 text-xs ml-1">· {l.role}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-xs text-slate-500">
              {isPendingSignup
                ? "New accounts are always added as Partner. Only Sean & Rina can promote to Leader or Platinum."
                : "Once saved, these become locked to your account. Your admin can edit them later."}
            </div>
          </div>

          {!hasCountry && (
            <div className="space-y-1.5" data-testid="onboard-country">
              <Label>Which country are you based in?</Label>
              <CountryFields value={country} onChange={setCountry} prefix="onboard" />
            </div>
          )}

          {isPendingSignup && <PrivacyChecks value={checks} onChange={setChecks} />}

          <div className="flex items-center justify-between pt-2">
            <button type="button" onClick={logout} className="text-xs text-slate-500 hover:text-slate-800">Sign out</button>
            <Button
              data-testid="onboard-submit" type="submit"
              disabled={busy || !team || !leader || (!hasCountry && !countryValid(country)) || (isPendingSignup ? namesInvalid : !name.trim()) || (isPendingSignup && (!checks.notice || !checks.pbo))}
              className="rounded-full bg-[#0A1F44] text-white h-11 px-6">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (isPendingSignup ? "Submit request" : "Continue")}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
